import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getAuditLogs, getAuditEntityTypes } from '../../../services/auditLogService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

// AKM POC §9.1 — İşlem Geçmişi: kategori ve eylem kodları backend AuditCategories / AuditActions ile AYNI.
export const AUDIT_CATEGORIES = ['auth', 'sensitiveView', 'export', 'permission', 'superAdminAccess', 'data', 'system'];
export const AUDIT_ACTIONS = [
  'create', 'update', 'delete', 'restore', 'hardDelete', 'bulkUpdate', 'bulkDelete',
  'login.succeeded', 'login.failed', 'logout',
  'mfa.succeeded', 'mfa.failed', 'mfa.enabled', 'mfa.reconfigured', 'mfa.disabled', 'mfa.resetByAdmin',
  'mfa.recoveryCodeUsed', 'mfa.recoveryCodesRegenerated', 'mfa.lockedOut',
  'password.changed', 'password.reset', 'password.set', 'password.expiredChanged', 'sso.login',
  'account.locked', 'account.unlocked',
  'sensitive.viewed', 'export', 'superAdmin.login', 'superAdmin.tenantSwitched',
  'retention.purged', 'fieldEncryption.migrated', 'fileRegistry.backfilled',
];
export const AUDIT_OUTCOMES = ['success', 'failure'];

// En çok görülen kayıtların alanları (Çalışan, Kişisel Bilgi, Pozisyon Ataması, İzin, rol / pozisyon rolü kapsam alanları,
// Şirket Ayarları güvenlik alanları) — `auditLog.fields.<ad>`; çevirisi olmayan alan ham adıyla görünür.
export const AUDIT_FIELD_NAMES = [
  'FirstName', 'LastName', 'AreaCode', 'PhoneNumber', 'CorporateEmail', 'PersonalEmail', 'Address', 'ProfilePicture',
  'CitizenId', 'CitizenIdHash', 'DateOfBirth', 'EmploymentDate', 'RegistrationNumber', 'CollarType', 'EmploymentType', 'Tz',
  'HolidayCalendarId', 'SubTenantId', 'EmployeeStatusId', 'PriorServiceDays', 'CandidateId', 'TerminationDate',
  'ExitReasonCode', 'IsErroneousRecord', 'ContractEndDate', 'ProbationEndDate', 'ExternalId', 'IsActive',
  'UserId', 'Gender', 'Pronoun', 'MaritalStatus', 'SpouseWorkingStatus', 'NumberOfChildren', 'Nationality', 'DisabilityLevel',
  'DisabilityLevelEncrypted', 'BloodType', 'EmergencyContactPerson1', 'EmergencyContactPhone1', 'EmergencyContactRelationship1',
  'EmergencyContactPerson2', 'EmergencyContactPhone2', 'EmergencyContactRelationship2', 'EducationLevel', 'EducationStatus',
  'LastInstitution', 'Faculty', 'BankAccountNumber', 'Iban', 'BankName',
  'PositionId', 'StartDate', 'EndDate', 'AssignmentType', 'ChangeReason',
  'LeaveVariationId', 'LeaveStart', 'LeaveEnd', 'Status', 'SignatureStatus', 'Cost', 'IsHourly', 'AfternoonExit', 'AfternoonEntry',
  'IsPaid', 'UserNotes', 'ManagerNotes', 'CurrentApprover', 'CurrentApproverType', 'RejectedUser', 'BulkLeaveId', 'CreatedVia',
  'TenantRoleId', 'DataScopeType', 'DataScopeTargetIds', 'Permission', 'TenantModuleId',
  'EmployeeId', 'Email', 'PasswordHash', 'PasswordChangedAt', 'Role', 'LeavePolicyId',
  'PasswordMinLength', 'PasswordRequireAlphanumeric', 'PasswordRequireSpecialCharacter', 'PasswordExpiryMonths',
  'PasswordExpiryStartedAt', 'MfaEnabled', 'MfaTrustedBrowserEnabled', 'SessionIdleTimeoutMinutes', 'LoginLockoutThreshold',
  'LoginLockoutWindowMinutes', 'LoginLockoutDurationMinutes', 'AuditRetentionMonths',
];

export const StaffBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #6366F1;
`;

export const OutcomeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: ${({ $outcome }) => ($outcome === 'failure' ? '#ef4444' : '#22c55e')};
`;

/** Etiket yardımcıları — çevirisi olmayan kod ham haliyle görünür. */
export function useAuditLabels() {
  const { t } = useTranslation();
  return useMemo(() => ({
    category: (code) => (code ? t(`auditLog.categories.${code}`, { defaultValue: code }) : ''),
    action: (code) => (code ? t(`auditLog.actions.${code}`, { defaultValue: code }) : ''),
    entityType: (name) => (name ? t(`auditLog.entityTypes.${name}`, { defaultValue: name }) : ''),
    outcome: (code) => (code ? t(`auditLog.outcomes.${code}`, { defaultValue: code }) : ''),
    field: (name) => (name ? t(`auditLog.fields.${name}`, { defaultValue: name }) : ''),
  }), [t]);
}

/** Alan etiketleri dışa aktarmaya: sunucu kaynağı 'field.<ad>' okur (yoksa ham ad). */
export function auditFieldExportLabels(labels) {
  const out = {};
  AUDIT_FIELD_NAMES.forEach((f) => { out[`field.${f}`] = labels.field(f); });
  return out;
}

/** Dışa aktarma etiketleri: sunucu kaynağının okuduğu 'category.<kod>', 'action.<kod>', 'outcome.<kod>', 'entityType.<ad>'. */
export function auditExportLabels(t, labels, entityTypes = []) {
  const out = {
    flexbeeStaff: t('auditLog.flexbeeStaff'),
    masked: t('auditLog.masked'),
    omitted: t('auditLog.omitted'),
  };
  AUDIT_CATEGORIES.forEach((c) => { out[`category.${c}`] = labels.category(c); });
  AUDIT_ACTIONS.forEach((a) => { out[`action.${a}`] = labels.action(a); });
  AUDIT_OUTCOMES.forEach((o) => { out[`outcome.${o}`] = labels.outcome(o); });
  entityTypes.forEach((e) => { out[`entityType.${e}`] = labels.entityType(e); });
  return out;
}

const dropdownColumn = (name, label, names, render) => ({
  name,
  label,
  options: {
    filter: true,
    sort: false,
    filterType: 'dropdown',
    filterOptions: { names, defaultOp: 'eq', renderValue: (v) => render(v) },
    customFilterListOptions: { render: (v) => `${label}: ${render(v)}` },
    customBodyRender: (v) => render(v) || '-',
  },
});

/**
 * İşlem Geçmişi listesi: kolon adları backend süzgeç parametreleriyle aynı (occurredAt[between], actorName[like],
 * subjectName[like], category[eq], action[eq], entityType[eq], ipAddress[like]). Okuma hatası "yüklenemedi" durumudur
 * (api.js GET reject etmez — sinyal res.data.status).
 */
const useAuditLogApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const labels = useAuditLabels();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 20, pageIndex: 0 });
  const [entityTypes, setEntityTypes] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    getAuditEntityTypes().then((res) => {
      if (res?.data?.status !== false && Array.isArray(res?.data?.data)) setEntityTypes(res.data.data);
    });
  }, []);

  useEffect(() => {
    if (!queryParams) return;
    setFetching(true);
    getAuditLogs(queryParams)
      .then((res) => {
        if (!res?.data || res.data.status === false) {
          setLoadFailed(true);
          setData([]);
          setPagination((p) => ({ ...p, totalRowCount: 0 }));
          return;
        }
        const result = res.data.data || {};
        const items = Array.isArray(result) ? result : (result.data || []);
        setLoadFailed(false);
        setData(items);
        setPagination({
          totalRowCount: result.totalRowCount ?? items.length,
          pageIndex: result.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result.pageSize || items.length || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    dateFilterColumn('occurredAt', t('auditLog.occurredAt'), i18n.language, { includeTime: true, sort: false }),
    {
      name: 'actorName',
      label: t('auditLog.actor'),
      options: {
        filter: true,
        sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (row?.actorIsSuperAdmin) return <StaffBadge>{t('auditLog.flexbeeStaff')}</StaffBadge>;
          return value || '-';
        },
      },
    },
    {
      name: 'subjectName',
      label: t('auditLog.subject'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    dropdownColumn('category', t('auditLog.category'), AUDIT_CATEGORIES, labels.category),
    dropdownColumn('action', t('auditLog.action'), AUDIT_ACTIONS, labels.action),
    dropdownColumn('entityType', t('auditLog.entityType'), entityTypes, labels.entityType),
    {
      name: 'entityId',
      label: t('auditLog.entityId'),
      options: { filter: false, sort: false, display: 'false', customBodyRender: (v) => v || '-' },
    },
    {
      name: 'ipAddress',
      label: t('auditLog.ip'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'outcome',
      label: t('auditLog.outcome'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? <OutcomeBadge $outcome={v}>{labels.outcome(v)}</OutcomeBadge> : '-'),
      },
    },
    {
      name: 'changeCount',
      label: t('auditLog.changeCount'),
      options: { filter: false, sort: false, display: 'false' },
    },
  ], [t, i18n.language, data, labels, entityTypes]);

  return { columns, data, fetching, loadFailed, pagination, entityTypes, labels, refresh };
};

export default useAuditLogApi;
